/**
 * Media strip. For Spotify: album art, track, transport controls - collapsed
 * to a slim bar during navigation; tapping art expands to a fuller card with
 * volume and "play here" (transfer playback into the car).
 *
 * For Audible (Settings > Media app) the strip is a launcher instead: Audible
 * has no public API - nothing outside Amazon's own apps may observe or
 * control playback - so the bar opens the Audible app and playback is driven
 * there or from the car's own media controls.
 */

import type { NowPlaying, SpotifyPlayer } from '../audio/spotify';
import { beginLogin, isAuthed } from '../audio/spotify';
import { hasSpotify } from '../core/config';
import { store } from '../core/store';
import { t } from '../core/i18n';

export class SpotifyBar {
  readonly el: HTMLElement;
  private expanded = false;
  private state: NowPlaying | null = null;

  constructor(
    parent: HTMLElement,
    private player: SpotifyPlayer,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'spotify';
    parent.appendChild(this.el);
    this.render();
  }

  update(state: NowPlaying | null): void {
    this.state = state;
    this.render();
  }

  /** Re-render with the current state - used when the media app setting changes. */
  refresh(): void {
    this.render();
  }

  private render(): void {
    if (store.get().settings.audioSource === 'audible') {
      this.renderAudible();
      return;
    }
    if (!hasSpotify()) {
      this.el.hidden = true;
      return;
    }
    this.el.hidden = false;

    if (!isAuthed()) {
      this.el.className = 'spotify spotify--connect';
      this.el.innerHTML = `
        <button class="spotify__connect-btn">${SPOTIFY_LOGO}<span>${t('sp.connect')}</span></button>`;
      this.el.querySelector('button')!.addEventListener('click', () => void beginLogin());
      return;
    }

    const s = this.state;
    this.el.className = `spotify ${this.expanded ? 'spotify--expanded' : ''}`;
    this.el.innerHTML = `
      <div class="spotify__bar">
        <button class="spotify__art" aria-label="Expand player">
          ${s?.albumArt ? `<img src="${s.albumArt}" alt=""/>` : SPOTIFY_LOGO}
        </button>
        <div class="spotify__track">
          <div class="spotify__name">${escapeHtml(s?.trackName || t('sp.nothing'))}</div>
          <div class="spotify__artist">${escapeHtml(s?.artists || (s?.deviceName ? t('sp.on', { device: s.deviceName }) : t('sp.hint')))}</div>
        </div>
        <div class="spotify__controls">
          <button class="icon-btn" data-act="prev" aria-label="Previous">${PREV_SVG}</button>
          <button class="icon-btn spotify__play" data-act="play" aria-label="Play or pause">
            ${s && !s.paused ? PAUSE_SVG : PLAY_SVG}
          </button>
          <button class="icon-btn" data-act="next" aria-label="Next">${NEXT_SVG}</button>
        </div>
      </div>
      ${
        this.expanded
          ? `<div class="spotify__extra">
              ${
                s && !s.playingHere
                  ? `<button class="btn btn--ghost btn--small" data-act="here">${t('sp.playHere')}</button>`
                  : `<span class="spotify__device">${t('sp.playingHere')}</span>`
              }
              <label class="spotify__vol">${VOL_SVG}
                <input type="range" min="0" max="100" value="80" data-act="vol"/>
              </label>
            </div>`
          : ''
      }`;

    this.el.querySelector('.spotify__art')!.addEventListener('click', () => {
      this.expanded = !this.expanded;
      this.render();
    });
    this.on('play', () => void this.player.togglePlay());
    this.on('next', () => void this.player.next());
    this.on('prev', () => void this.player.previous());
    this.on('here', () => void this.player.transferHere());
    this.el.querySelector('[data-act=vol]')?.addEventListener('change', (e) => {
      void this.player.setVolume(Number((e.target as HTMLInputElement).value) / 100);
    });
  }

  private renderAudible(): void {
    this.el.hidden = false;
    this.el.className = 'spotify';
    this.el.innerHTML = `
      <div class="spotify__bar">
        <button class="spotify__art" aria-label="${t('aud.open')}">${AUDIBLE_LOGO}</button>
        <div class="spotify__track">
          <div class="spotify__name">Audible</div>
          <div class="spotify__artist">${t('aud.hint')}</div>
        </div>
        <div class="spotify__controls">
          <button class="icon-btn spotify__play" data-act="open-audible" aria-label="${t('aud.open')}">
            ${OPEN_SVG}
          </button>
        </div>
      </div>`;
    const open = () => openAudible();
    this.el.querySelector('.spotify__art')!.addEventListener('click', open);
    this.on('open-audible', open);
  }

  private on(act: string, fn: () => void): void {
    this.el.querySelector(`[data-act=${act}]`)?.addEventListener('click', fn);
  }
}

/**
 * Hand over to the Audible app. On Android an intent URL launches the app when
 * installed and falls back to the web library when not; elsewhere the plain
 * scheme is the only handle there is (it simply does nothing if not installed).
 */
function openAudible(): void {
  if (/Android/i.test(navigator.userAgent)) {
    window.location.href =
      'intent://library/#Intent;scheme=audible;package=com.audible.application;' +
      'S.browser_fallback_url=https%3A%2F%2Fwww.audible.com%2Flibrary;end';
  } else {
    window.location.href = 'audible://library';
  }
}

const SPOTIFY_LOGO = `<svg viewBox="0 0 24 24" class="spotify__logo"><circle cx="12" cy="12" r="10" fill="#1DB954"/><path d="M7 9.5c3.2-1 7-.6 9.6 1M7.5 12.3c2.6-.8 5.6-.4 7.8.9M8 15c2-.6 4.3-.3 6 .7" stroke="#07090d" stroke-width="1.5" stroke-linecap="round" fill="none"/></svg>`;
const AUDIBLE_LOGO = `<svg viewBox="0 0 24 24" class="spotify__logo"><circle cx="12" cy="12" r="10" fill="#F8991C"/><path d="M12 8.2 a4.2 4.2 0 0 0 -4.2 4.2 v3 h2.4 v-3 a1.8 1.8 0 0 1 3.6 0 v3 h2.4 v-3 A4.2 4.2 0 0 0 12 8.2 Z" fill="#07090d"/><path d="M5.5 8.5 C7 6.4 9.4 5.2 12 5.2 s5 1.2 6.5 3.3" fill="none" stroke="#07090d" stroke-width="1.5" stroke-linecap="round"/></svg>`;
const OPEN_SVG = `<svg viewBox="0 0 24 24"><path d="M9 5 h10 v10 M19 5 L10 14 M5 9 v10 h10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const PLAY_SVG = `<svg viewBox="0 0 24 24"><path d="M8 5 L19 12 L8 19 Z" fill="currentColor"/></svg>`;
const PAUSE_SVG = `<svg viewBox="0 0 24 24"><path d="M7 5 h3.4 v14 H7 Z M13.6 5 H17 v14 h-3.4 Z" fill="currentColor"/></svg>`;
const NEXT_SVG = `<svg viewBox="0 0 24 24"><path d="M6 5 L14 12 L6 19 Z M15.5 5 h2.5 v14 h-2.5 Z" fill="currentColor"/></svg>`;
const PREV_SVG = `<svg viewBox="0 0 24 24"><path d="M18 5 L10 12 L18 19 Z M6 5 h2.5 v14 H6 Z" fill="currentColor"/></svg>`;
const VOL_SVG = `<svg viewBox="0 0 24 24"><path d="M4 9 v6 h4 l5 4 V5 L8 9 Z" fill="currentColor"/><path d="M16 9 a4 4 0 0 1 0 6" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/></svg>`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
